<template>
  <section class="ai-visibility-citations-tab">
    <loading-state v-if="loading" label="Loading citations data..."/>
    <error-state
        v-else-if="loadError"
        :message="loadError"
        retry-label="Retry citations"
        @retry="loadCitations"
    />
    <empty-state
        v-else-if="!citations.rows.length"
        title="No citations data yet"
        description="Run this AI Visibility tracker to see source and citation data."
    />
    <template v-else>
      <citation-metrics v-if="citations.metrics" :metrics="citations.metrics"/>

      <div class="ai-visibility-citations-tab__panel">
        <citation-sources-toolbar
            title="Every source AI relied on"
            :subtitle="tableSubtitle"
            :search="search"
            :group-by-website="groupByWebsite"
            @update:search="handleSearchUpdate"
            @update:group-by-website="handleGroupByWebsiteUpdate"
            @export="handleExport"
        />
        <citation-sources-table
            :rows="paginatedRows"
            :group-by-website="groupByWebsite"
            :sort-key="sortKey"
            :sort-direction="sortDirection"
            @sort-change="handleSortChange"
            @select-citation="openCitationSidebar"
        />
      </div>
      <table-pagination
          v-if="pageCount > 1"
          :page="currentPage"
          :page-count="pageCount"
          @input="currentPage = $event"
      />
      <citation-details-sidebar
          v-if="selectedCitation"
          :open="isSidebarOpen"
          :citation="selectedCitation"
          @close="closeCitationSidebar"
          @after-close="clearSelectedCitation"
          @view-prompts="handleViewPrompts"
          @open-missing-prompts="handleOpenMissingPrompts"
      />
    </template>
  </section>
</template>

<script>
import {getAiVisibilityCitations} from '../../../api'
import LoadingState from '../../ui/states/LoadingState.vue'
import ErrorState from '../../ui/states/ErrorState.vue'
import EmptyState from '../../ui/states/EmptyState.vue'
import CitationMetrics from './components/CitationMetrics.vue'
import CitationSourcesToolbar from './components/CitationSourcesToolbar.vue'
import CitationSourcesTable from './components/CitationSourcesTable.vue'
import CitationDetailsSidebar from './components/CitationDetailsSidebar.vue'
import TablePagination from '../../ui/table/TablePagination.vue'
import {
  buildCitationSourcesCsv,
  CITATION_SOURCES_CSV_FILENAME,
  downloadCsv,
} from './citationSourcesCsv'

const PAGE_SIZE = 10
const OPPORTUNITY_RANKS = {
  high: 3,
  medium: 2,
  low: 1,
}

function createEmptyCitations() {
  return {
    total: 0,
    rows: [],
    metrics: null,
  }
}

export default {
  name: 'AiVisibilityCitationsTab',
  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    CitationMetrics,
    CitationSourcesToolbar,
    CitationSourcesTable,
    CitationDetailsSidebar,
    TablePagination,
  },
  props: {
    siteId: {
      type: [Number, String],
      required: true,
    },
    trackerId: {
      type: [Number, String],
      required: true,
    },
    detail: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filterOptions: {
      type: Object,
      default() {
        return {}
      },
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      citations: createEmptyCitations(),
      search: '',
      groupByWebsite: false,
      sortKey: 'citations',
      sortDirection: 'desc',
      currentPage: 1,
      selectedCitation: null,
      isSidebarOpen: false,
    }
  },
  computed: {
    tableSubtitle() {
      return `Ordered by citations - highest first · ${this.citations.total} sources total`;
    },
    filteredRows() {
      const query = this.search.trim().toLowerCase()
      if (!query) {
        return this.citations.rows
      }

      return this.citations.rows.filter(row => {
        const website = (row.website || '').toLowerCase()
        const title = (row.citedPage?.title || '').toLowerCase()
        const url = (row.citedPage?.url || '').toLowerCase()
        return website.indexOf(query) > -1 ||
            title.indexOf(query) > -1 ||
            url.indexOf(query) > -1
      })
    },
    sortedRows() {
      return this.sortRows(this.filteredRows)
    },
    groupedRows() {
      const groupedByWebsite = this.filteredRows.reduce((groups, row) => {
        const website = row.website || ''
        if (!groups[website]) {
          groups[website] = []
        }
        groups[website].push(row)
        return groups
      }, {})

      const rows = Object.keys(groupedByWebsite).map(website => {
        const children = this.sortRows(groupedByWebsite[website])
        const citationValue = children.reduce((total, row) => {
          return total + Number(row.citations?.value || 0)
        }, 0)

        return {
          id: `citation-website-${website}`,
          website,
          pagesCount: children.length,
          citations: {
            value: citationValue,
            max: citationValue,
          },
          mentionStatus: this.getWebsiteMentionStatus(children),
          opportunity: this.getWebsiteOpportunity(children),
          lastSeen: this.getLatestLastSeen(children),
          children,
        }
      })

      const maxCitations = Math.max(...rows.map(row => row.citations.value), 0)
      rows.forEach(row => {
        row.citations.max = maxCitations
      })

      return this.sortRows(rows)
    },
    tableRows() {
      return this.groupByWebsite ? this.groupedRows : this.sortedRows
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.tableRows.length / PAGE_SIZE))
    },
    paginatedRows() {
      const startIndex = (this.currentPage - 1) * PAGE_SIZE
      return this.tableRows.slice(startIndex, startIndex + PAGE_SIZE)
    },
    paginationStart() {
      if (!this.tableRows.length) {
        return 0
      }
      return ((this.currentPage - 1) * PAGE_SIZE) + 1
    },
    paginationEnd() {
      return Math.min(this.currentPage * PAGE_SIZE, this.tableRows.length)
    },
    paginationSummary() {
      if (!this.tableRows.length) {
        return 'Showing 0 sources'
      }
      return `Showing ${this.paginationStart}-${this.paginationEnd} of ${this.tableRows.length} sources`
    },
  },
  watch: {
    siteId: 'loadCitations',
    trackerId: 'loadCitations',
    'filters.range': 'loadCitations',
    'filters.model': 'loadCitations',
    pageCount(pageCount) {
      if (this.currentPage > pageCount) {
        this.currentPage = pageCount
      }
    },
  },
  beforeMount() {
    this.loadCitations()
  },
  methods: {
    loadCitations() {
      this.loading = true
      this.loadError = ''
      this.closeCitationSidebar()

      return getAiVisibilityCitations(this.siteId, this.trackerId, this.filters)
          .then((citations) => {
            const payload = citations?.data || {}
            this.citations = {
              total: payload.total || 0,
              rows: payload.rows || [],
              metrics: this.buildCitationMetrics(payload.metrics),
            }
          })
          .catch(() => {
            this.citations = createEmptyCitations()
            this.loadError = 'Could not load AI Visibility citations data.'
          })
          .finally(() => {
            this.loading = false
          })
    },
    handleSearchUpdate(value) {
      this.search = value
      this.currentPage = 1
    },
    handleGroupByWebsiteUpdate(value) {
      this.groupByWebsite = value
      this.currentPage = 1
    },
    handleSortChange({key, direction}) {
      this.sortKey = key
      this.sortDirection = direction
      this.currentPage = 1
    },
    handleExport() {
      downloadCsv(
          buildCitationSourcesCsv(this.citations.rows),
          CITATION_SOURCES_CSV_FILENAME
      )
    },
    openCitationSidebar(row) {
      if (!row || !row.citedPage) {
        return
      }

      this.selectedCitation = row
      this.$nextTick(() => {
        this.isSidebarOpen = true
      })
    },
    closeCitationSidebar() {
      this.isSidebarOpen = false
    },
    clearSelectedCitation() {
      this.selectedCitation = null
    },
    handleViewPrompts(payload = {}) {
      this.navigateToQuestions({
        questionIds: payload.questionIds || [],
      })
    },
    handleOpenMissingPrompts(payload = {}) {
      this.navigateToQuestions({
        status: 'missing',
        questionIds: payload.questionIds || [],
      })
    },
    navigateToQuestions(extraQuery = {}) {
      const citationUrl = this.selectedCitation?.citedPage?.url
      const query = {...extraQuery}
      if (citationUrl) {
        query.citationUrl = citationUrl
      }
      if (Array.isArray(query.questionIds)) {
        const questionIds = this.getUniqueQuestionIds(query.questionIds)
        if (questionIds.length) {
          query.questionIds = questionIds.join(',')
        } else {
          query.questionIds = ''
        }
      }

      this.closeCitationSidebar()
      if (this.embedded) {
        this.$emit('navigate-tab', {
          name: 'AiVisibilityQuestions',
          query,
        })
        return
      }
      this.$router.push({
        name: 'AiVisibilityQuestions',
        params: {
          siteId: String(this.siteId),
          trackerId: String(this.trackerId),
        },
        query,
      })
    },
    getUniqueQuestionIds(questionIds) {
      return questionIds.reduce((uniqueIds, questionId) => {
        if (questionId === null || questionId === undefined || questionId === '') {
          return uniqueIds
        }
        const normalizedId = String(questionId)
        if (!uniqueIds.includes(normalizedId)) {
          uniqueIds.push(normalizedId)
        }
        return uniqueIds
      }, [])
    },
    sortRows(rows) {
      const sortedRows = rows.slice()
      const directionFactor = this.sortDirection === 'asc' ? 1 : -1

      sortedRows.sort((first, second) => {
        const firstValue = this.getSortValue(first, this.sortKey)
        const secondValue = this.getSortValue(second, this.sortKey)

        if (firstValue === secondValue) {
          return (second.citations.value - first.citations.value)
        }

        return firstValue > secondValue ? directionFactor : -directionFactor
      })

      return sortedRows
    },
    getSortValue(row, key) {
      if (key === 'citations') {
        return row.citations.value
      }
      if (key === 'opportunity') {
        return OPPORTUNITY_RANKS[row.opportunity.impact] || 0
      }
      if (key === 'mentioned') {
        return row.mentionStatus?.tone === 'success' ? 1 : 0
      }
      return 0
    },
    getWebsiteMentionStatus(rows) {
      const mentionedRow = rows.find(row => row.mentionStatus?.tone === 'success')
      if (mentionedRow) {
        return mentionedRow.mentionStatus
      }
      return rows[0]?.mentionStatus || {label: '', tone: 'default'}
    },
    getWebsiteOpportunity(rows) {
      return rows.reduce((strongestOpportunity, row) => {
        const currentRank = OPPORTUNITY_RANKS[row.opportunity?.impact] || 0
        const strongestRank = OPPORTUNITY_RANKS[strongestOpportunity?.impact] || 0
        return currentRank > strongestRank ? row.opportunity : strongestOpportunity
      }, rows[0]?.opportunity || {impact: 'low', label: 'Low impact'})
    },
    getLatestLastSeen(rows) {
      return rows.reduce((latestValue, row) => {
        if (!latestValue) {
          return row.lastSeen
        }
        if (!row.lastSeen) {
          return latestValue
        }
        const latestTime = new Date(latestValue).getTime()
        const rowTime = new Date(row.lastSeen).getTime()
        return rowTime > latestTime ? row.lastSeen : latestValue
      }, null)
    },
    buildCitationMetrics(metrics) {
      if (!metrics) {
        return null
      }

      const uniqueWebsites = Number(metrics.uniqueWebsites || 0)
      const uniqueCitationUrls = Number(metrics.uniqueCitationUrls || 0)
      const totalCitations = Number(metrics.totalCitations || 0)
      const highOpportunitySources = Number(metrics.highOpportunitySources || 0)
      const trends = metrics.trends || {}

      return {
        uniqueWebsites,
        highOpportunitySources,
        uniqueCitationUrls,
        totalCitations,
        trends: {
          uniqueWebsites: trends.uniqueWebsites === null ? null : Number(trends.uniqueWebsites || 0),
          uniqueCitationUrls: trends.uniqueCitationUrls === null ? null : Number(trends.uniqueCitationUrls || 0),
          totalCitations: trends.totalCitations === null ? null: Number(trends.totalCitations || 0),
          highOpportunitySources: trends.highOpportunitySources === null ? null : Number(trends.highOpportunitySources || 0),
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-citations-tab {
  margin-top: 24px;
  padding-bottom: 32px;
}

.ai-visibility-citations-tab__panel {
  overflow: hidden;
  border: 1px solid #DADADA;
  border-radius: 8px;
  background: var(--v-cardBg-base);
  margin-top: 24px;
}
</style>
